import React from "react";
import {
    FlaskConical,
    Cog,
    Leaf,
    Recycle,
    GraduationCap,
    Users,
    Compass,
    Store,
    Factory,
    Sprout,
} from "lucide-react";

const INDUSTRIAL_IMG =
    "https://images.pexels.com/photos/10514991/pexels-photo-10514991.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const RURAL_IMG =
    "https://images.unsplash.com/photo-1758390286700-06f93fddec45?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwxfHxydXJhbCUyMEluZGlhbiUyMHdvbWVuJTIwd29ya2luZ3xlbnwwfHx8fDE3Nzc2NjMwMzh8MA&ixlib=rb-4.1.0&q=85";

const INDUSTRIAL = [
    {
        Icon: FlaskConical,
        title: "Custom biotech R&D",
        body: "Bespoke research tailored to your process, feedstock and KPIs.",
    },
    {
        Icon: Cog,
        title: "Process optimization",
        body: "Biological interventions that lift yield and efficiency.",
    },
    {
        Icon: Leaf,
        title: "Sustainable manufacturing",
        body: "Replace petrochemical steps with bio-based pathways.",
    },
    {
        Icon: Recycle,
        title: "Waste-to-value",
        body: "Turn by-products into high-margin, commercially viable streams.",
    },
];

const RURAL = [
    {
        Icon: GraduationCap,
        title: "Skill development",
        body: "Practical training in bio-based product development for women & youth.",
    },
    {
        Icon: Users,
        title: "Mentorship & incubation",
        body: "Ground-level guidance that turns rural innovators into founders.",
    },
    {
        Icon: Compass,
        title: "Commercialization",
        body: "Clear pathways from prototype to certified, market-ready product.",
    },
    {
        Icon: Store,
        title: "Market & distribution",
        body: "Linking village-made bio-products to urban and export channels.",
    },
];

/* Decorative category card ------------------------------------------------- */
const CategoryHeader = ({ tag, title, tagline, image, Icon, reverse }) => (
    <div
        className={`relative rounded-[2rem] overflow-hidden border border-border shadow-sm ${
            reverse ? "md:order-last" : ""
        }`}
    >
        <div className="relative aspect-[16/10] md:aspect-[3/4]">
            <img
                src={image}
                alt={`${title} imagery`}
                className="absolute inset-0 h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-brand-cypress/90 via-brand-cypress/40 to-transparent" />
            <div className="absolute inset-0 p-8 md:p-10 flex flex-col justify-between">
                <div className="flex items-center gap-2">
                    <span className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-black/20 backdrop-blur-md px-3.5 py-1.5 text-[10px] uppercase tracking-[0.22em] text-brand-bone">
                        <Icon className="h-3.5 w-3.5 text-[hsl(var(--brand-copper))]" />
                        {tag}
                    </span>
                </div>
                <div>
                    <h3 className="font-display text-3xl sm:text-4xl md:text-[42px] font-medium text-brand-bone leading-[1.02] tracking-tight break-words hyphens-auto">
                        {title}
                    </h3>
                    <p className="mt-3 text-sm md:text-[15px] text-brand-bone/80 max-w-sm leading-relaxed">
                        {tagline}
                    </p>
                </div>
            </div>
        </div>
    </div>
);

const ItemRow = ({ Icon, title, body, index }) => (
    <div
        data-testid={`solution-item-${title.toLowerCase().replace(/\s+/g, "-")}`}
        className="group flex gap-5 items-start rounded-2xl p-5 -mx-5 border border-transparent hover:border-border hover:bg-card/60 transition-all duration-300"
    >
        <div className="shrink-0 h-12 w-12 rounded-2xl bg-primary/15 text-primary flex items-center justify-center group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
            <Icon className="h-5 w-5" />
        </div>
        <div className="flex-1">
            <div className="flex items-baseline gap-3">
                <span className="font-display text-sm text-muted-foreground tabular-nums">
                    0{index + 1}
                </span>
                <h4 className="font-display text-xl md:text-2xl font-medium tracking-tight">
                    {title}
                </h4>
            </div>
            <p className="mt-1.5 text-sm md:text-[15px] leading-relaxed text-muted-foreground">
                {body}
            </p>
        </div>
    </div>
);

export const Solutions = () => {
    return (
        <section
            id="solutions"
            data-testid="solutions-section"
            className="section-pad relative bg-card/40 overflow-hidden"
        >
            <div
                aria-hidden
                className="blob bg-[hsl(var(--brand-copper))]/15"
                style={{ width: 520, height: 520, top: -160, right: -120 }}
            />

            <div className="container-x relative">
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
                    <div>
                        <p className="overline">
                            <span className="eyebrow-line" />
                            What we offer
                        </p>
                        <h2 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-medium leading-[1.02]">
                            Solutions that{" "}
                            <em className="not-italic text-primary">work with nature</em>.
                        </h2>
                    </div>
                    <p className="max-w-md text-base md:text-lg leading-relaxed text-muted-foreground">
                        Two connected tracks — one for industrial bio-process partners, one
                        for grassroots entrepreneurs.
                    </p>
                </div>

                {/* Industrial bloc */}
                <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 items-stretch">
                    <div className="md:col-span-5">
                        <CategoryHeader
                            tag="Track 01 · Industrial"
                            title="Industrial Solutions"
                            tagline="For manufacturers transitioning to cleaner, more efficient, bio-based production."
                            image={INDUSTRIAL_IMG}
                            Icon={Factory}
                        />
                    </div>
                    <div className="md:col-span-7 flex flex-col justify-center gap-2">
                        {INDUSTRIAL.map((item, i) => (
                            <ItemRow key={item.title} {...item} index={i} />
                        ))}
                    </div>
                </div>

                {/* Divider */}
                <div className="my-20 md:my-28 relative">
                    <div className="h-px bg-border w-full" />
                    <span className="absolute left-1/2 -translate-x-1/2 -top-3 bg-card px-4 text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
                        Industry meets community
                    </span>
                </div>

                {/* Rural bloc (reversed) */}
                <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 items-stretch">
                    <div className="md:col-span-7 flex flex-col justify-center gap-2 md:order-1">
                        {RURAL.map((item, i) => (
                            <ItemRow key={item.title} {...item} index={i} />
                        ))}
                    </div>
                    <div className="md:col-span-5 md:order-2">
                        <CategoryHeader
                            tag="Track 02 · Rural"
                            title="Rural Entrepreneurship"
                            tagline="Seeding opportunity in villages — from skill-building to market access."
                            image={RURAL_IMG}
                            Icon={Sprout}
                        />
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Solutions;
